import React, { useState } from 'react';
import TabNavigation from '../../components/ui/TabNavigation';
import PrivacyToggle from '../../components/ui/PrivacyToggle';

import QuickActionFloat from '../../components/ui/QuickActionFloat';
import PrivateInputPanel from './components/PrivateInputPanel';
import ConversationPanel from './components/ConversationPanel';
import GuidedDiscussionCard from './components/GuidedDiscussionCard';
import CompatibilityInsights from './components/CompatibilityInsights';
import Icon from '../../components/AppIcon';


const AICommunicationHub = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('conversations');

  // Mock data removed - ready for live data integration
  const mockConversations = [];
  const mockGuidedDiscussions = [];
  const mockCompatibilityInsights = {
    overallScore: 0,
    overallSummary: "",
    scores: [],
    recommendations: []
  };

  const handlePrivateSubmit = (data) => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      console.log('Private thought submitted:', data);
    }, 1500);
  };

  const handleRespond = (conversationId, responseId) => {
    console.log('Response sent:', { conversationId, responseId });
  };

  const handleStartDiscussion = (discussionId) => {
    console.log('Starting guided discussion:', discussionId);
  };

  return (
    <div className="min-h-screen bg-background">
      <TabNavigation />
      <PrivacyToggle />
      <QuickActionFloat />
      <main className="pt-[60px] pb-20 lg:pt-[80px] lg:pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 md:mb-8 lg:mb-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-semibold text-foreground mb-2 md:mb-3">
              AI Communication Hub
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-measure">
              Share your intimate thoughts privately with AI assistance. Your partner won't see your direct input—the AI will help facilitate comfortable, constructive conversations.
            </p>
            <div className="mt-4 p-3 md:p-4 bg-primary/5 border border-primary/20 rounded-lg flex items-start space-x-3">
              <Icon name="Sparkles" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm md:text-base text-foreground font-medium mb-1">
                  AI-Powered Desire Translation
                </p>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Powered by Anthropic Claude, your intimate preferences are privately translated into elegant, partner-appropriate language before sending. Your raw thoughts remain completely confidential.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6 md:mb-8 border-b border-border">
            <button
              onClick={() => setActiveTab('conversations')}
              className={`
                px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium font-caption
                border-b-2 transition-therapeutic
                ${activeTab === 'conversations' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}
              `
              }>

              Active Conversations
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-accent/10 text-accent">
                {mockConversations?.filter((c) => c?.status !== 'resolved')?.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('guided')}
              className={`
                px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium font-caption
                border-b-2 transition-therapeutic
                ${activeTab === 'guided' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}
              `
              }>

              Guided Discussions
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={`
                px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium font-caption
                border-b-2 transition-therapeutic
                ${activeTab === 'insights' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}
              `
              }>

              Compatibility Insights
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
            <div className="lg:col-span-1">
              <PrivateInputPanel
                onSubmit={handlePrivateSubmit}
                isLoading={isSubmitting} />

            </div>

            <div className="lg:col-span-2">
              {activeTab === 'conversations' &&
              <ConversationPanel
                conversations={mockConversations}
                onRespond={handleRespond} />

              }

              {activeTab === 'guided' &&
              <div className="space-y-4 md:space-y-6">
                  <div className="bg-card border border-border rounded-lg p-4 md:p-6 shadow-therapeutic">
                    <div className="flex items-center space-x-3 mb-4 md:mb-6">
                      <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-md">
                        <svg className="w-5 h-5 md:w-6 md:h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-xl md:text-2xl lg:text-3xl font-heading font-semibold text-foreground">
                          Guided Discussions
                        </h2>
                        <p className="text-sm md:text-base text-muted-foreground font-caption">
                          AI-facilitated conversations for deeper connection
                        </p>
                      </div>
                    </div>
                  </div>

                  {mockGuidedDiscussions?.map((discussion) =>
                <GuidedDiscussionCard
                  key={discussion?.id}
                  discussion={discussion}
                  onStart={handleStartDiscussion} />

                )}
                </div>
              }

              {activeTab === 'insights' &&
              <CompatibilityInsights insights={mockCompatibilityInsights} />
              }
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default AICommunicationHub;